import { useState, useEffect, useRef, useContext, memo, useMemo, useCallback, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';

import { AppContext } from './AppContext.tsx';
import OpenAISvg from '../assets/openai-clean.svg?react';
import AnthropicSvg from '../assets/anthropic-clean.svg?react';
import GeminiSvg from '../assets/gemini-clean.svg?react';
import XaiSvg from '../assets/xai.svg?react';
import OpenRouterSvg from '../assets/openrouter.svg?react';
import ChevronDownIcon from '../components/icons/chevrondown.svg?react'
import InfoIcon from '../components/icons/infocircle.svg?react'
import Spinner from './Spinner.jsx'
import GearIcon from './icons/settingsgearfill.svg?react'
import CheckIcon from './icons/check.svg?react'
import PlusIcon from './icons/plus.svg?react'
import './CmdMenu/CmdMenu.css'

/**
 * ModelSelector - A dropdown component for selecting AI models
 * 
 * This component provides a user interface for selecting between different AI models
 * (Claude, GPT, Gemini, etc.). It displays the available models in a dropdown menu
 * with model logos, names, and descriptions.
 * 
 * @example
 * // Basic usage in main input area
 * <ModelSelector position="top-right" isMain={true} />
 * 
 * @example
 * // Standalone usage with custom models (e.g., in onboarding)
 * <ModelSelector
 *   models={customModels}
 *   selectedModel={selectedModel}
 *   setSelectedModel={handleModelChange}
 * />
 * 
 * @component
 */
const ModelSelector = ({
  position = 'top-right',
  isMain = false,
  // Optional props for standalone usage (e.g., in onboarding)
  models: propModels = null,
  selectedModel: propSelectedModel = null,
  setSelectedModel: propSetSelectedModel = null,
}) => {
  // Context integration - uses AppContext for model data and state management
  const context = useContext(AppContext)
  const {
    enabledModels,
    favoriteModelId,
    modelSelectorRef,
    initializingUI,
  } = context;

  // Use props if provided, otherwise fall back to context
  // Use enabledModels (filtered by user preferences) instead of all models
  const rawModels = propModels || enabledModels || [];
  const selectedModel = propSelectedModel || context?.selectedModel;
  const setSelectedModel = propSetSelectedModel || context?.setSelectedModel;

  // Sort models: favorite first, then the rest
  const models = useMemo(() => {
    if (!favoriteModelId) return rawModels;

    const favorite = rawModels.find(m => m.id === favoriteModelId);
    const others = rawModels.filter(m => m.id !== favoriteModelId);

    return favorite ? [favorite, ...others] : rawModels;
  }, [rawModels, favoriteModelId]);

  // Dropdown state management
  const [isOpen, setIsOpen] = useState(false); // Controls dropdown visibility
  const [highlightedIndex, setHighlightedIndex] = useState(-1); // Tracks keyboard navigation highlight
  const localRef = useRef(null);
  const itemRefs = useRef([]);
  const dropdownRef = useRef(null);
  const selectorRef = isMain ? modelSelectorRef : localRef;

  // Handles model selection and closes dropdown
  const selectModel = useCallback((model) => {
    setSelectedModel(model);
    setIsOpen(false);
  }, [setSelectedModel, setIsOpen]);

  // Register the toggle function with the context if this is the main/primary instance
  // This allows the dropdown to be controlled from other parts of the app (e.g., keyboard shortcuts)
  useEffect(() => {
    if (isMain && modelSelectorRef.current) {
      // Add a toggle method to the ref that can be called from context
      modelSelectorRef.current.toggle = () => setIsOpen(prev => !prev);
    }
  }, [isMain, modelSelectorRef]);

  // Keyboard navigation setup - highlights first item when opened, clears when closed
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(0);
      // Once opened, focus the dropdown so arrow keys work
      setTimeout(() => dropdownRef.current?.focus(), 0)
    } else {
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex].scrollIntoView({
        block: 'nearest',
        inline: 'nearest'
      })
    }
  }, [highlightedIndex])


  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, { passive: true });
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, selectorRef]);

  // Keyboard navigation handlers (Arrow keys, Enter, Escape)
  useEffect(() => {
    if (!isOpen) return;

    // Only models now - no tabs
    const items = models.map(x => ({ type: 'model', data: x }));

    const onKeyDown = (e) => {
      // Clamp highlightedIndex to valid range before navigation
      const clampedIndex = Math.min(Math.max(highlightedIndex, 0), items.length - 1);
      if (highlightedIndex !== clampedIndex) {
        setHighlightedIndex(clampedIndex);
      }

      const last = items.length - 1;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(i => i < last ? i + 1 : 0);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(i => i > 0 ? i - 1 : last);
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < items.length && items[highlightedIndex]) {
            selectModel(items[highlightedIndex].data)
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          break;
      }
    };

    const dr = dropdownRef.current;
    dr?.addEventListener('keydown', onKeyDown);
    return () => dr?.removeEventListener('keydown', onKeyDown);
  }, [isOpen, highlightedIndex, models, selectModel]);

  // Removed unused Web Search tools configuration

  // Returns the appropriate logo component based on model family
  const getLogo = useCallback((type, family) => {
    let Logo;
    switch (family?.toLowerCase()) {
      case 'claude':
        Logo = AnthropicSvg;
        break;
      case 'gemini':
        Logo = GeminiSvg;
        break;
      case 'xai':
      case 'grok':
        Logo = XaiSvg;
        break;
      case 'openrouter':
        Logo = OpenRouterSvg;
        break;
      default:
        Logo = OpenAISvg;
    }
    return <Logo className="size-4 text-primary flex-shrink-0" aria-label={`${family} logo`} />;
  }, []);

  const POSITIONS = {
    'top-right': 'bottom-full right-0 mb-2',
    'top-left': 'bottom-full left-0 mb-2',
  }

  const positionClass = POSITIONS[position] || POSITIONS['top-right']

  // Main trigger button that opens/closes the dropdown
  const triggerButton = useMemo(() => {
    const isDisabled = initializingUI || models.length === 0;

    const variants = {
      hidden: { opacity: 0, scale: 0.5 },
      visible: { opacity: 1, scale: 1 },
    }

    const transition = {
      duration: 0,
      delay: 0
    }

    return (
      <button
        aria-label="Select AI model"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`flex flex-row items-center gap-spacing-2 ${isDisabled ? 'cursor-not-allowed opacity-50' : 'hover:cursor-pointer'
          } focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-lg p-1`}
        onClick={isDisabled ? null : (e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        disabled={isDisabled}
      >
        <AnimatePresence mode="wait" initial={false}>
          {initializingUI ? (
            <motion.span
              key="loading"
              transition={transition}
              variants={variants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <Spinner size="sm" />
            </motion.span>
          ) : (
            <motion.span
              key="showModel"
              transition={{
                duration: 0,
                delay: 0
              }}
              variants={variants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <span className="text-dls-body">{selectedModel?.display_name || 'Select model'}</span>
            </motion.span>
          )}
        </AnimatePresence>
        <ChevronDownIcon
          className={`size-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''
            } ${isDisabled ? '' : 'hover:cursor-pointer'}`}
        />
      </button>
    );
  }, [selectedModel, isOpen, models, initializingUI]);

  // Renders the list of available models in the dropdown
  const modelItems = useMemo(() => {
    const items = models.map(x => ({
      id: x.id,
      name: x.display_name,
      model: x,
      type: 'model',
      onSelectFn: selectModel
    }));

    return (
      <div
        role="listbox"
        aria-label="Available AI models"
        className="flex flex-col hover:cursor-pointer max-h-80 overflow-y-auto overflow-x-hidden custom-scrollbar p-spacing-1 w-full"
      >
        {items.map((i, idx) => (
          <Item
            name={i.name}
            type={i.type}
            key={`${i.type}-${i.id}`}
            ref={el => itemRefs.current[idx] = el}
            model={i.model}
            isSelected={selectedModel?.id === i.id}
            isHighlighted={idx === highlightedIndex}
            getLogo={getLogo}
            onSelect={i.onSelectFn}
            onHighlight={() => setHighlightedIndex(idx)}
          />
        ))}
      </div>
    )
  }, [models, selectedModel, getLogo, selectModel, highlightedIndex]);

  // The dropdown container with all content (models list)
  const dropdown = useMemo(() => (
    <div
      ref={dropdownRef}
      tabIndex={-1}
      className={`
        ${positionClass}
        focus:outline-none
        focus:ring-0
        absolute
        text-primary
        bg-bg-dropdown
        rounded-lg
        flex
        flex-col
        w-64
        border border-border-default
        transition-all
        duration-300
        ease-out
        ${isOpen ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95 pointer-events-none'}
      `}
    >
      {modelItems}
    </div>
  ), [isOpen, positionClass, modelItems]);

  return (
    <div ref={selectorRef} className="relative inline-block">
      {triggerButton}
      {dropdown}
    </div>
  );
};

// Individual model item component within the dropdown
const Item = memo(forwardRef(({
  name,
  type,
  model,
  isSelected,
  isHighlighted,
  getLogo,
  onSelect,
  onHighlight
}, ref) => (
  <button
    role="option"
    aria-selected={isSelected}
    ref={ref}
    onClick={() => onSelect(model)}
    onMouseEnter={onHighlight}
    className={`
      h-[32px] w-full flex flex-row items-center justify-start px-spacing-2 py-spacing-1
      gap-spacing-2 cursor-pointer rounded-lg transition-all duration-200
      focus:outline-none focus-visible:ring-2 focus-visible:ring-accent text-left
      ${isHighlighted ? 'bg-bg-hover' : ''}
      ${!isSelected && !isHighlighted ? 'hover:bg-bg-hover' : ''}
    `}
  >
    {getLogo(type, model?.model_family)}
    <div className="flex flex-row flex-1 text-left items-center justify-between">
      <span className="truncate text-left text-primary">
        {name}
      </span>
      <div className="flex flex-row items-center gap-spacing-2">
        {isSelected && <CheckIcon className="size-4 flex-shrink-0 text-accent" />}
      </div>
    </div>
  </button>
)));

Item.displayName = 'Item';

ModelSelector.propTypes = {
  /** Position of the dropdown relative to the trigger button ('top-right' | 'top-left') */
  position: PropTypes.string,

  /** Whether this is the primary model selector instance (enables global keyboard shortcuts) */
  isMain: PropTypes.bool,

  /** Array of available model objects (optional - uses context if not provided) */
  models: PropTypes.array,

  /** Currently selected model object (optional - uses context if not provided) */
  selectedModel: PropTypes.object,

  /** Callback function to update selected model (optional - uses context if not provided) */
  setSelectedModel: PropTypes.func,
};

export default memo(ModelSelector);
